import React, { useState, useEffect } from 'react';
import { TicketType } from '../types';
import { ticketTypeService } from '../services/api';

interface TicketTypeManagementProps {
  refreshTrigger?: number;
}

const TicketTypeManagement: React.FC<TicketTypeManagementProps> = ({ refreshTrigger }) => {
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<TicketType>>({
    type_id: '',
    price: 0,
    fee_percentage: 0,
    description: '',
  });

  useEffect(() => {
    fetchTicketTypes();
  }, [refreshTrigger]);

  const fetchTicketTypes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await ticketTypeService.getTicketTypes();
      setTicketTypes(response);
    } catch (err: any) {
      console.error('Failed to fetch ticket types:', err);
      setError(err.response?.data?.message || 'Failed to load ticket types');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setError(null);
      await ticketTypeService.create(formData);
      setFormData({
        type_id: '',
        price: 0,
        fee_percentage: 0,
        description: '',
      });
      setIsCreating(false);
      fetchTicketTypes();
    } catch (err: any) {
      console.error('Failed to create ticket type:', err);
      setError(err.response?.data?.message || 'Failed to create ticket type');
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    
    try {
      setError(null);
      await ticketTypeService.update(editingId, formData);
      setEditingId(null);
      setFormData({
        type_id: '',
        price: 0,
        fee_percentage: 0,
        description: '',
      });
      fetchTicketTypes();
    } catch (err: any) {
      console.error('Failed to update ticket type:', err);
      setError(err.response?.data?.message || 'Failed to update ticket type');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this ticket type?')) return;
    
    try {
      setError(null);
      await ticketTypeService.delete(id);
      fetchTicketTypes();
    } catch (err: any) {
      console.error('Failed to delete ticket type:', err);
      setError(err.response?.data?.message || 'Failed to delete ticket type');
    }
  };

  const startEdit = (ticketType: TicketType) => {
    setEditingId(ticketType.id);
    setFormData({
      type_id: ticketType.id,
      price: ticketType.price,
      fee_percentage: ticketType.fee_percentage || 0,
      description: ticketType.description || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
    setFormData({
      type_id: '',
      price: 0,
      fee_percentage: 0,
      description: '',
    });
  };


  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Ticket Types Management</h2>
        <button
          onClick={() => setIsCreating(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add New Ticket Type
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Create/Edit Form */}
      {(isCreating || editingId) && (
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingId ? 'Edit Ticket Type' : 'Create New Ticket Type'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type ID
              </label>
              <input
                type="text"
                value={formData.type_id || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, type_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., ticket_25"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price (EUR)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.price || 0}
                onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fee Percentage (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.fee_percentage || 0}
                onChange={(e) => setFormData(prev => ({ ...prev, fee_percentage: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your profit margin (e.g., 5 for 5%)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Price (Base + Fee)
              </label>
              <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 font-semibold">
                €{((formData.price || 0) + ((formData.price || 0) * (formData.fee_percentage || 0) / 100)).toFixed(2)}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Optional description for this ticket type"
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={cancelEdit}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={editingId ? handleUpdate : handleCreate}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {ticketTypes.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No ticket types found</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Base Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fee %
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ticketTypes.map((ticketType) => {
                  const basePrice = parseFloat(String(ticketType.price));
                  const feePercentage = parseFloat(String(ticketType.fee_percentage || 0));
                  const feeAmount = (basePrice * feePercentage) / 100;
                  const totalPrice = basePrice + feeAmount;

                  return (
                  <tr key={ticketType.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">
                      {ticketType.id}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      €{basePrice.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {feePercentage.toFixed(2)}%
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      €{totalPrice.toFixed(2)}
                      {feeAmount > 0 && (
                        <span className="text-xs text-gray-500 ml-1">
                          (+€{feeAmount.toFixed(2)})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {ticketType.description || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => startEdit(ticketType)}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center space-x-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDelete(ticketType.id)}
                          className="px-3 py-1 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center space-x-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TicketTypeManagement;
